import { useCallback, useEffect, useState } from 'react'
import { api, ErroApi } from '../lib/api'
import { Secao, Vazio, Carregando, Erro, Folha } from '../componentes/ui'
import './modelos.css'

/**
 * Os formulários do prontuário.
 *
 * ─── POR QUE ESTA TELA COMEÇA COM O CATÁLOGO ───────────────────────
 *
 * A clínica nasce sem nenhum modelo, de propósito. Mas prontuário vazio
 * tem um problema que sala vazia não tem: sem modelo, ninguém registra
 * NADA — nem a primeira evolução. Então a tela não abre num botão
 * "criar formulário do zero", que é o que faria a pessoa fechar e ligar
 * para o suporte. Abre no que ela pode pegar pronto e ajustar depois.
 *
 * ─── A REGRA QUE A TELA PRECISA TORNAR ÓBVIA ───────────────────────
 *
 * Modelo publicado NÃO se edita: editar cria a versão seguinte, e a
 * anterior é aposentada, nunca apagada. É o que permite abrir uma ficha
 * de 2024 e ver o formulário de 2024. Um sistema que deixasse editar o
 * ativo mostraria o registro antigo com campos que não existiam quando
 * ele foi escrito — e aí o prontuário deixa de ser prova.
 */

type Modelo = {
  id: string; slug: string; nome: string
  especie: string; versao: number
  status: 'RASCUNHO' | 'ATIVO' | 'APOSENTADO'
  esquema: Esquema; publicado_em: string | null
  fichas: string
}

type Campo = {
  chave: string; rotulo: string; tipo: string
  obrigatorio?: boolean; min?: number; max?: number; opcoes?: string[]
  passo?: number; unidade?: string
}
type Bloco = { titulo: string; campos: Campo[] }
type Esquema = { blocos?: Bloco[] }

type ItemCatalogo = {
  slug: string; nome: string; especie: string
  descricao: string | null; ja_tem: boolean
}

const ESPECIE: Record<string, string> = {
  AVALIACAO: 'Avaliação', EVOLUCAO: 'Evolução', ANAMNESE: 'Anamnese',
  ALTA: 'Alta', CONSENTIMENTO: 'Termo', LAUDO: 'Laudo',
}

const TIPO: Record<string, string> = {
  texto: 'Texto curto', texto_longo: 'Texto longo', numero: 'Número',
  escala: 'Escala', selecao: 'Escolha', booleano: 'Sim/Não', data: 'Data',
}

const TIPOS = Object.keys(TIPO)

export function Modelos({ podeEditar }: { podeEditar: boolean }) {
  const [itens, setItens] = useState<Modelo[]>([])
  const [catalogo, setCatalogo] = useState<ItemCatalogo[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [vendoCatalogo, setVendoCatalogo] = useState(false)
  const [editando, setEditando] = useState<Modelo | null>(null)
  const [novo, setNovo] = useState(false)

  const carregar = useCallback(async () => {
    setCarregando(true); setErro(null)
    try {
      const [m, c] = await Promise.all([
        api<{ itens: Modelo[] }>('/v1/modelos?todos=true'),
        api<{ itens: ItemCatalogo[] }>('/v1/catalogo'),
      ])
      setItens(m.itens); setCatalogo(c.itens)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar os modelos.')
    } finally { setCarregando(false) }
  }, [])

  useEffect(() => { void carregar() }, [carregar])

  async function importar(slug: string) {
    try {
      await api(`/v1/catalogo/${slug}/importar`, { metodo: 'POST' })
      await carregar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível importar.')
    }
  }

  // Só a versão no ar e os rascunhos entram na lista principal. As
  // aposentadas ficam na folha de versões do próprio modelo: mostrá-las
  // aqui faria a lista crescer para sempre e esconder o que está valendo.
  const noAr = itens.filter((m) => m.status !== 'APOSENTADO')
  const naoImportados = catalogo.filter((c) => !c.ja_tem)

  return (
    <>
      <Erro mensagem={erro} />

      {carregando ? <Carregando /> : (
        <>
          <Secao
            titulo="Formulários do prontuário"
            descricao="O que aparece na tela quando alguém abre uma ficha do paciente."
            acao={podeEditar && (
              <div className="md-acoes">
                {naoImportados.length > 0 && (
                  <button className="botao botao--fantasma"
                          onClick={() => setVendoCatalogo(true)}>
                    Pegar pronto ({naoImportados.length})
                  </button>
                )}
                <button className="botao botao--fantasma" onClick={() => setNovo(true)}>
                  Criar do zero
                </button>
              </div>
            )}
          >
            {noAr.length === 0 ? (
              <Vazio
                mensagem="Nenhum formulário ainda."
                sugestao="Sem pelo menos um, ninguém consegue escrever uma evolução — nem a primeira. Comece pegando os prontos e ajuste depois: eles passam a ser seus."
                acao={podeEditar && naoImportados.length > 0 && (
                  <button className="botao" onClick={() => setVendoCatalogo(true)}>
                    Ver os formulários prontos
                  </button>
                )}
              />
            ) : (
              <ul className="md-lista">
                {noAr.map((m) => (
                  <li key={m.id} className={`md-item is-${m.status.toLowerCase()}`}>
                    <div className="md-item__corpo">
                      <span className="md-item__nome">
                        {m.nome}
                        <span className="md-selo">{ESPECIE[m.especie] ?? m.especie}</span>
                        {m.status === 'RASCUNHO' && (
                          <span className="md-selo md-selo--rascunho">rascunho</span>
                        )}
                      </span>
                      <span className="ajuda">
                        versão {m.versao}
                        {' · '}
                        {contarCampos(m.esquema)} campos
                        {Number(m.fichas) > 0 && ` · ${m.fichas} fichas preenchidas`}
                      </span>
                    </div>
                    {podeEditar && (
                      <button className="md-abrir" onClick={() => setEditando(m)}>
                        {m.status === 'ATIVO' ? 'Ver e versionar' : 'Editar'}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Secao>

          {/* A regra da imutabilidade explicada uma vez, onde ela morde. */}
          {noAr.some((m) => m.status === 'ATIVO' && Number(m.fichas) > 0) && (
            <p className="md-nota">
              Formulário publicado não muda mais. Ajustar cria a versão seguinte
              e aposenta a atual — as fichas já preenchidas continuam abrindo com
              o formulário da época delas, que é o que faz o prontuário valer
              como prova.
            </p>
          )}
        </>
      )}

      {vendoCatalogo && (
        <Catalogo itens={naoImportados} aoImportar={importar}
                  aoFechar={() => setVendoCatalogo(false)} />
      )}

      {(novo || editando) && (
        <Editor modelo={editando}
                aoFechar={() => { setNovo(false); setEditando(null) }}
                aoSalvar={async () => { setNovo(false); setEditando(null); await carregar() }} />
      )}
    </>
  )
}

const contarCampos = (e: Esquema) =>
  (e?.blocos ?? []).reduce((s, b) => s + (b.campos?.length ?? 0), 0)

/* ══ o catálogo ════════════════════════════════════════════════════ */

function Catalogo({ itens, aoImportar, aoFechar }: {
  itens: ItemCatalogo[]
  aoImportar: (slug: string) => Promise<void>
  aoFechar: () => void
}) {
  const [ocupado, setOcupado] = useState<string | null>(null)

  return (
    <Folha titulo="Formulários prontos"
           descricao="Escolhidos pela especialidade da sua clínica. Depois de pegar, o formulário é seu — pode mudar tudo."
           aoFechar={aoFechar}
           acoes={<button className="botao" onClick={aoFechar}>Fechar</button>}>
      {itens.length === 0 ? (
        <Vazio mensagem="Você já pegou todos os que servem para a sua especialidade." />
      ) : (
        <ul className="md-catalogo">
          {itens.map((c) => (
            <li key={c.slug}>
              <div>
                <strong>{c.nome}</strong>
                <span className="md-selo">{ESPECIE[c.especie] ?? c.especie}</span>
                {c.descricao && <p className="ajuda">{c.descricao}</p>}
              </div>
              <button className="botao botao--fantasma botao--pequeno"
                      disabled={ocupado === c.slug}
                      onClick={async () => {
                        setOcupado(c.slug)
                        await aoImportar(c.slug)
                        setOcupado(null)
                      }}>
                {ocupado === c.slug ? 'Pegando…' : 'Pegar'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Folha>
  )
}

/* ══ o editor ══════════════════════════════════════════════════════ */

/**
 * Editar um modelo ATIVO cria a versão seguinte — não altera a atual.
 * O botão diz isso com todas as letras, porque é a única coisa nesta
 * tela que a pessoa pode fazer sem entender e se arrepender depois.
 */
function Editor({ modelo, aoFechar, aoSalvar }: {
  modelo: Modelo | null
  aoFechar: () => void
  aoSalvar: () => Promise<void>
}) {
  const criando = !modelo
  const publicado = modelo?.status === 'ATIVO'

  const [nome, setNome] = useState(modelo?.nome ?? '')
  const [slug, setSlug] = useState(modelo?.slug ?? '')
  const [especie, setEspecie] = useState(modelo?.especie ?? 'EVOLUCAO')
  const [blocos, setBlocos] = useState<Bloco[]>(
    () => modelo?.esquema?.blocos?.map((b) => ({ ...b, campos: [...b.campos] }))
          ?? [{ titulo: 'Atendimento', campos: [] }])
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  function mudarCampo(bi: number, ci: number, patch: Partial<Campo>) {
    setBlocos((bs) => bs.map((b, i) => i !== bi ? b : {
      ...b, campos: b.campos.map((c, j) => j !== ci ? c : { ...c, ...patch }),
    }))
  }

  function addCampo(bi: number) {
    setBlocos((bs) => bs.map((b, i) => i !== bi ? b : {
      ...b,
      campos: [...b.campos, { chave: `campo_${b.campos.length + 1}`,
                              rotulo: '', tipo: 'texto_longo' }],
    }))
  }

  function tirarCampo(bi: number, ci: number) {
    setBlocos((bs) => bs.map((b, i) => i !== bi ? b : {
      ...b, campos: b.campos.filter((_, j) => j !== ci),
    }))
  }

  async function salvar(e: React.FormEvent) {
    e.preventDefault()
    setErro(null)

    const vazio = blocos.some((b) => b.campos.some((c) => !c.rotulo.trim()))
    if (vazio) { setErro('Todo campo precisa de um rótulo — é o que a pessoa lê na tela.'); return }
    if (contarCampos({ blocos }) === 0) { setErro('Um formulário sem campo não registra nada.'); return }

    setSalvando(true)
    try {
      const esquema = { blocos }
      if (criando || publicado) {
        // Modelo publicado é imutável: salvar cria v+1 e aposenta a
        // anterior. É o mesmo POST — o servidor calcula a versão.
        await api('/v1/modelos', {
          metodo: 'POST',
          corpo: {
            slug: criando ? gerarSlug(nome) : slug,
            nome: nome.trim(), especie, esquema, publicar: true,
          },
        })
      } else {
        await api(`/v1/modelos/${modelo!.id}`, {
          metodo: 'PATCH', corpo: { nome: nome.trim(), esquema },
        })
      }
      await aoSalvar()
    } catch (err) {
      setErro(err instanceof ErroApi ? err.message : 'Não foi possível salvar.')
      setSalvando(false)
    }
  }

  async function publicar() {
    if (!modelo) return
    setSalvando(true)
    try {
      await api(`/v1/modelos/${modelo.id}`, { metodo: 'PATCH', corpo: { status: 'ATIVO' } })
      await aoSalvar()
    } catch (err) {
      setErro(err instanceof ErroApi ? err.message : 'Não foi possível publicar.')
      setSalvando(false)
    }
  }

  return (
    <Folha titulo={criando ? 'Novo formulário' : nome}
           descricao={publicado
             ? `Versão ${modelo!.versao}, no ar. Salvar cria a versão ${modelo!.versao + 1} e aposenta esta — as fichas já escritas continuam com o formulário delas.`
             : criando ? 'Blocos e campos. O que estiver aqui é o que aparece na ficha do paciente.'
             : `Rascunho da versão ${modelo!.versao}. Ninguém preenche até publicar.`}
           aoFechar={aoFechar}
           acoes={
             <>
               {modelo?.status === 'RASCUNHO' && (
                 <button type="button" className="botao botao--fantasma"
                         onClick={publicar} disabled={salvando}>
                   Publicar
                 </button>
               )}
               <button type="submit" form="form-modelo" className="botao" disabled={salvando}>
                 {salvando ? 'Salvando…'
                   : publicado ? `Salvar como versão ${modelo!.versao + 1}`
                   : 'Salvar'}
               </button>
             </>
           }>
      <form id="form-modelo" onSubmit={salvar} className="md-form">
        {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

        <div className="md-cabecalho">
          <div className="campo">
            <label htmlFor="md-nome">Nome do formulário</label>
            <input id="md-nome" className="entrada" required autoFocus
                   maxLength={120} value={nome}
                   onChange={(e) => setNome(e.target.value)} />
          </div>
          <div className="campo">
            <label htmlFor="md-esp">Serve para</label>
            <select id="md-esp" className="entrada" value={especie}
                    disabled={!criando}
                    onChange={(e) => setEspecie(e.target.value)}>
              {Object.entries(ESPECIE).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            {!criando && (
              <span className="ajuda">
                Não muda depois: ficha já preenchida com este formulário deixaria
                de bater com o tipo.
              </span>
            )}
          </div>
        </div>

        {blocos.map((b, bi) => (
          <fieldset key={bi} className="md-bloco">
            <legend>
              <input className="md-bloco__titulo" value={b.titulo}
                     aria-label={`Título do bloco ${bi + 1}`}
                     onChange={(e) => setBlocos((bs) =>
                       bs.map((x, i) => i === bi ? { ...x, titulo: e.target.value } : x))} />
            </legend>

            {b.campos.length === 0 ? (
              <p className="ajuda">Bloco vazio. Some da ficha se ficar assim.</p>
            ) : (
              <ul className="md-campos">
                {b.campos.map((c, ci) => (
                  <li key={ci}>
                    <input className="entrada" placeholder="O que a pessoa lê"
                           value={c.rotulo} aria-label="Rótulo do campo"
                           onChange={(e) => mudarCampo(bi, ci, { rotulo: e.target.value })} />
                    <select className="entrada" value={c.tipo} aria-label="Tipo do campo"
                            onChange={(e) => mudarCampo(bi, ci, { tipo: e.target.value })}>
                      {TIPOS.map((t) => <option key={t} value={t}>{TIPO[t]}</option>)}
                    </select>
                    <label className="md-obrig">
                      <input type="checkbox" checked={c.obrigatorio ?? false}
                             onChange={(e) => mudarCampo(bi, ci, { obrigatorio: e.target.checked })} />
                      <span>obrigatório</span>
                    </label>
                    <button type="button" className="md-tirar"
                            aria-label={`Tirar o campo ${c.rotulo || ci + 1}`}
                            onClick={() => tirarCampo(bi, ci)}>×</button>

                    {/* Escala ganha faixa, passo e unidade. Sem o passo,
                        toda escala era 0–10 de 1 em 1, e TUG (s), Borg,
                        marcha (m/s) e goniometria (°) — que precisam de
                        decimal ou de outra faixa — não davam para montar. */}
                    {c.tipo === 'escala' && (
                      <div className="md-escala">
                        <label>de <input type="number" className="entrada" value={c.min ?? 0}
                               onChange={(e) => mudarCampo(bi, ci, { min: Number(e.target.value) })} /></label>
                        <label>a <input type="number" className="entrada" value={c.max ?? 10}
                               onChange={(e) => mudarCampo(bi, ci, { max: Number(e.target.value) })} /></label>
                        <label>passo <input type="number" className="entrada" step="0.01" min="0.01"
                               value={c.passo ?? 1}
                               onChange={(e) => mudarCampo(bi, ci, { passo: Number(e.target.value) || 1 })} /></label>
                        <label>unidade <input className="entrada" placeholder="s, °, m/s" maxLength={8}
                               value={c.unidade ?? ''}
                               onChange={(e) => mudarCampo(bi, ci, { unidade: e.target.value })} /></label>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}

            <button type="button" className="md-add" onClick={() => addCampo(bi)}>
              + campo
            </button>
          </fieldset>
        ))}

        <button type="button" className="md-add md-add--bloco"
                onClick={() => setBlocos((bs) => [...bs, { titulo: 'Novo bloco', campos: [] }])}>
          + bloco
        </button>
      </form>
    </Folha>
  )
}

/** Slug a partir do nome: minúsculo, sem acento, hífen no lugar do resto. */
function gerarSlug(nome: string): string {
  return nome.normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '').slice(0, 60) || 'formulario'
}
